import React, { useMemo, useEffect } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Card, Row, Col, OverlayTrigger, Tooltip, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart,
  faHandshake,
  faSyncAlt,
  faChartLine,
  faBoxes,
  faExclamationTriangle,
  faInfoCircle,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import { useProviders } from '../../../hooks/api/clientsProvides';

const ClientDashboardChart = () => {
  // Obtener datos directamente del hook
  const { 
    providersDashboardData, 
    isRefreshing, 
    isAutoRefreshing, 
    forceUpdate 
  } = useProviders();
  
  // Determinar si se está cargando (manual o automático)
  const isLoading = isRefreshing || isAutoRefreshing;
  
  // Debug: Log cuando cambian los datos
  useEffect(() => {
    console.log('=== CLIENT DASHBOARD CHART - DATOS ACTUALIZADOS ===');
    console.log('providersDashboardData:', providersDashboardData);
    console.log('isLoading:', isLoading);
    console.log('forceUpdate:', forceUpdate);
    console.log('isRefreshing:', isRefreshing);
    console.log('isAutoRefreshing:', isAutoRefreshing);
  }, [providersDashboardData, isLoading, forceUpdate, isRefreshing, isAutoRefreshing]);
  
  // Configuración base de los charts
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0,0,0,0.1)'
        },
        ticks: {
          color: '#666'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#666'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8
      }
    }
  };

  // Procesar los datos del dashboard
  const dashboardMetrics = useMemo(() => {
    console.log('=== PROCESANDO DASHBOARD METRICS ===');
    console.log('providersDashboardData:', providersDashboardData);
    console.log('providersDashboardData?.generalTotals:', providersDashboardData?.generalTotals);
    console.log('forceUpdate:', forceUpdate); // Incluir forceUpdate para que se recalcule
    
    if (!providersDashboardData?.generalTotals) {
      console.log('No hay generalTotals, retornando valores por defecto');
      return {
        entrante: 0,
        saliente: 0,
        devProveedor: 0,
        devCliente: 0,
        ajusteSalida: 0,
        ajusteEntrada: 0,
        facturado: 0,
        porFacturar: 0
      };
    }

    const totals = providersDashboardData.generalTotals;
    console.log('totals encontrados:', totals);
    
    const metrics = {
      entrante: totals.entrante || 0,
      saliente: totals.saliente || 0,
      devProveedor: totals.devProveedor || 0,
      devCliente: totals.devCliente || 0,
      ajusteSalida: totals.ajusteSalida || 0,
      ajusteEntrada: totals.ajusteEntrada || 0,
      facturado: totals.facturado || 0,
      porFacturar: totals.porFacturar || 0
    };
    
    console.log('metrics calculadas:', metrics);
    console.log('=============================');
    
    return metrics;
  }, [providersDashboardData, forceUpdate]); // Incluir forceUpdate en las dependencias

  // Calcular métricas derivadas
  const unidadesDisponibles = dashboardMetrics.entrante - dashboardMetrics.saliente + 
                             dashboardMetrics.devCliente - dashboardMetrics.devProveedor - 
                             dashboardMetrics.ajusteSalida + dashboardMetrics.ajusteEntrada;
  
  // Usar directamente el valor de porFacturar de generalTotals
  const cantidadPorFacturarReal = dashboardMetrics.porFacturar;
  
  const totalMovimientos = dashboardMetrics.entrante + dashboardMetrics.saliente + 
                          dashboardMetrics.devProveedor + dashboardMetrics.devCliente;
  
  const porcentajeFacturacion = dashboardMetrics.saliente > 0 ? 
    ((dashboardMetrics.facturado / dashboardMetrics.saliente) * 100).toFixed(1) : 0;

  // Datos para el gráfico de barras - Movimientos principales
  const movimientosData = {
    labels: ['Unds. Otorgadas', 'Unds. Vendidas', 'Dev. Proveedor', 'Dev. Cliente'],
    datasets: [{
      label: 'Unidades',
      data: [
        dashboardMetrics.entrante,
        dashboardMetrics.saliente,
        dashboardMetrics.devProveedor,
        dashboardMetrics.devCliente
      ],
      backgroundColor: [
        '#10b981', // Verde para entradas
        '#3b82f6', // Azul para salidas  
        '#f59e0b', // Amarillo para dev. proveedor
        '#ef4444'  // Rojo para dev. cliente
      ],
      borderColor: [
        '#059669',
        '#2563eb', 
        '#d97706',
        '#dc2626'
      ],
      borderWidth: 2,
      borderRadius: 12,
      borderSkipped: false
    }]
  };

  // Datos para el gráfico de dona - Facturación
  const facturacionData = {
    labels: ['Facturado', 'Por Facturar'],
    datasets: [{
      data: [dashboardMetrics.facturado, cantidadPorFacturarReal],
      backgroundColor: ['#10b981', '#f59e0b'],
      borderColor: ['#059669', '#d97706'],
      borderWidth: 3,
      hoverOffset: 8
    }]
  };

  // Función para abrir WhatsApp
  const openWhatsApp = () => {
    const phoneNumber = "584241224684"; // Número actualizado
    const message = encodeURIComponent(`Hola, necesito ayuda con mi dashboard de inventario. Proveedor: ${providersDashboardData?.ownerName || 'No especificado'}`);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  // Tooltips informativos
  const tooltips = {
    entrante: "Unidades de productos que han sido otorgadas por la empresa a nuestro inventario",
    saliente: "Unidades de productos que han sido vendidas a clientes finales",
    unidadesDisponibles: "Unidades disponibles en inventario (+entrada -salida +dev de cliente -dev. a proveedor -ajustes de salida +ajustes de entrada)",
    devCliente: "Unidades devueltas por clientes que han regresado al inventario",
    devProveedor: "Unidades devueltas hacia el proveedor que han salido del inventario",
    ajusteEntrada: "Correcciones positivas realizadas al inventario por diferencias encontradas",
    ajusteSalida: "Correcciones negativas realizadas al inventario por diferencias encontradas",
    facturado: "Unidades que ya han sido facturadas y cobradas",
    porFacturar: "Unidades entregadas pendientes de facturación",
    cantidadPorFacturarReal: "Unidades entregadas pendientes de facturación al proveedor"
  };

  // Componente para renderizar valor con loader
  const renderValueWithLoader = (value, className = "") => {
    if (isLoading) {
      return (
        <div className={`d-flex align-items-center justify-content-center ${className}`}>
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted small">Actualizando...</span>
        </div>
      );
    }
    return value.toLocaleString();
  };

  // Verificar si el proveedor no es a consignación
  // Distinguir entre datos no cargados (undefined) y explícitamente no consignación (false)
  const isNonConsignmentProvider = providersDashboardData && 
    providersDashboardData.consignment === false && 
    !providersDashboardData.generalTotals;

  return (
    <div className="client-dashboard-charts">
      {/* Mensaje para proveedores no a consignación */}
      {isNonConsignmentProvider && (
        <div className="non-consignment-message mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center p-4">
              <div className="mb-3">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  size="3x" 
                  className="text-info"
                />
              </div>
              <h4 className="text-info mb-3">Proveedor no a consignación</h4>
              <p className="text-muted mb-3">
                Este proveedor no maneja inventario a consignación, por lo que no se muestran datos de dashboard.
              </p>
              <p className="text-muted small">
                Los datos de inventario y métricas solo están disponibles para proveedores que manejan productos a consignación.
              </p>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Indicador de estado de actualización */}
      {isLoading && (
        <div className="update-status-indicator mb-3">
          <div className="d-flex align-items-center justify-content-center p-2 bg-light rounded">
            <Spinner animation="border" size="sm" className="me-2 text-primary" />
            <span className="text-primary small fw-medium">
              {isRefreshing ? 'Actualizando datos manualmente...' : 'Actualizando datos automáticamente...'}
            </span>
          </div>
        </div>
      )}

      {/* Contenido del dashboard solo para proveedores a consignación */}
      {!isNonConsignmentProvider && (
        <>
          {/* Tarjetas de métricas principales con tooltips */}
          <Row className="mb-4" style={{ rowGap: '1rem' }}>
        <Col lg={3} md={6} className="mb-3">
          <Card className={`h-100 border-0 shadow-sm hover-shadow-lg transition-all ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Body className="text-center p-3">
              <div className="mb-2">
                <FontAwesomeIcon 
                  icon={faShoppingCart} 
                  size="2x" 
                  className="text-primary fa-icon"
                />
              </div>
              <h2 className="mb-1 text-primary fw-bold">
                {renderValueWithLoader(dashboardMetrics.saliente)}
              </h2>
              <div className="d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0 me-2 small">Unds. Vendidas</p>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-saliente">
                      {tooltips.saliente}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-info cursor-pointer" 
                    size="sm"
                  />
                </OverlayTrigger>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className={`h-100 border-0 shadow-sm hover-shadow-lg transition-all ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Body className="text-center p-3">
              <div className="mb-2">
                <FontAwesomeIcon 
                  icon={faHandshake} 
                  size="2x" 
                  className="text-success fa-icon"
                />
              </div>
              <h2 className="mb-1 text-success fw-bold">
                {renderValueWithLoader(dashboardMetrics.entrante)}
              </h2>
              <div className="d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0 me-2 small">Unds. Otorgadas</p>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-entrante">
                      {tooltips.entrante}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-info cursor-pointer" 
                    size="sm"
                  />
                </OverlayTrigger>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className={`h-100 border-0 shadow-sm hover-shadow-lg transition-all ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Body className="text-center p-3">
              <div className="mb-2">
                <FontAwesomeIcon 
                  icon={faBoxes} 
                  size="2x" 
                  className="text-info fa-icon"
                />
              </div>
              <h2 className="mb-1 text-info fw-bold">
                {renderValueWithLoader(unidadesDisponibles)}
              </h2>
              <div className="d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0 me-2 small">Unds. Disponibles</p>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-unidades-disponibles">
                      {tooltips.unidadesDisponibles}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-info cursor-pointer" 
                    size="sm"
                  />
                </OverlayTrigger>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={3} md={6} className="mb-3">
          <Card className={`h-100 border-0 shadow-sm hover-shadow-lg transition-all ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Body className="text-center p-3">
              <div className="mb-2">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  size="2x" 
                  className="text-warning fa-icon"
                />
              </div>
              <h2 className="mb-1 text-warning fw-bold">
                {renderValueWithLoader(cantidadPorFacturarReal)}
              </h2>
              <div className="d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0 me-2 small">Unds. Pendientes por facturar al proveedor</p>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-por-facturar">
                      {tooltips.cantidadPorFacturarReal}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-info cursor-pointer" 
                    size="sm"
                  />
                </OverlayTrigger>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Nueva fila para los recuadros de devoluciones separados */}
      <Row className="mb-4" style={{ rowGap: '1rem' }}>
        <Col lg={6} md={6} className="mb-3">
          <Card className={`h-100 border-0 shadow-sm hover-shadow-lg transition-all ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Body className="text-center p-3">
              <div className="mb-2">
                <FontAwesomeIcon 
                  icon={faSyncAlt} 
                  size="2x" 
                  className="text-danger fa-icon"
                />
              </div>
              <h2 className="mb-1 text-danger fw-bold">
                {renderValueWithLoader(dashboardMetrics.devCliente)}
              </h2>
              <div className="d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0 me-2 small">Unds. Dev. al Cliente</p>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-dev-cliente">
                      {tooltips.devCliente}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-info cursor-pointer" 
                    size="sm"
                  />
                </OverlayTrigger>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6} md={6} className="mb-3">
          <Card className={`h-100 border-0 shadow-sm hover-shadow-lg transition-all ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Body className="text-center p-3">
              <div className="mb-2">
                <FontAwesomeIcon 
                  icon={faSyncAlt} 
                  size="2x" 
                  className="text-warning fa-icon"
                />
              </div>
              <h2 className="mb-1 text-warning fw-bold">
                {renderValueWithLoader(dashboardMetrics.devProveedor)}
              </h2>
              <div className="d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0 me-2 small">Unds. Dev. al Proveedor</p>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-dev-proveedor">
                      {tooltips.devProveedor}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-info cursor-pointer" 
                    size="sm"
                  />
                </OverlayTrigger>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráficos mejorados */}
      <Row className="mb-5">
        {/* Gráfico de movimientos principales */}
        <Col lg={8} className="mb-4">
          <Card className={`h-100 border-0 shadow-sm ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Header className="bg-transparent border-0 py-4">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faChartLine} className="me-3 text-primary" size="lg" />
                <h4 className="mb-0 fw-bold">Movimientos de Inventario</h4>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <div style={{ height: '350px' }}>
                {isLoading ? (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" size="lg" />
                      <p className="mt-3 text-muted">Actualizando gráfico...</p>
                    </div>
                  </div>
                ) : (
                  <Bar data={movimientosData} options={chartOptions} key={`movimientos-${forceUpdate}`} />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico de facturación */}
        <Col lg={4} className="mb-4">
          <Card className={`h-100 border-0 shadow-sm ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Header className="bg-transparent border-0 py-4">
              <h4 className="mb-0 fw-bold">Estado de Facturación</h4>
            </Card.Header>
            <Card.Body className="p-4">
              <div style={{ height: '350px' }}>
                {isLoading ? (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" size="lg" />
                      <p className="mt-3 text-muted">Actualizando gráfico...</p>
                    </div>
                  </div>
                ) : (
                  <Doughnut data={facturacionData} options={doughnutOptions} key={`facturacion-${forceUpdate}`} />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Fila adicional para ajustes y resumen mejorada */}
      <Row className="mb-5">
        {/* Sección de Ajustes mejorada */}
        <Col lg={6} className="mb-4">
          <Card className={`h-100 border-0 shadow-sm ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Header className="bg-transparent border-0 py-4">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faSyncAlt} className="me-3 text-info" size="lg" />
                <h4 className="mb-0 fw-bold">Ajustes de Inventario</h4>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="g-4">
                <Col md={6}>
                  <div className="p-4 bg-gradient rounded-4 text-center position-relative overflow-hidden h-100" 
                       style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                    <div className="text-white">
                      <FontAwesomeIcon icon={faBoxes} size="2x" className="mb-3 opacity-75" />
                      <h3 className="mb-2 fw-bold">
                        {renderValueWithLoader(dashboardMetrics.ajusteEntrada)}
                      </h3>
                      <div className="d-flex align-items-center justify-content-center">
                        <small className="opacity-90 me-2">Ajustes de Entrada</small>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-ajuste-entrada">
                              {tooltips.ajusteEntrada}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon 
                            icon={faInfoCircle} 
                            className="text-white opacity-75 cursor-pointer" 
                            size="sm"
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-4 bg-gradient rounded-4 text-center position-relative overflow-hidden h-100"
                       style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                    <div className="text-white">
                      <FontAwesomeIcon icon={faShoppingCart} size="2x" className="mb-3 opacity-75" />
                      <h3 className="mb-2 fw-bold">
                        {renderValueWithLoader(dashboardMetrics.ajusteSalida)}
                      </h3>
                      <div className="d-flex align-items-center justify-content-center">
                        <small className="opacity-90 me-2">Ajustes de Salida</small>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-ajuste-salida">
                              {tooltips.ajusteSalida}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon 
                            icon={faInfoCircle} 
                            className="text-white opacity-75 cursor-pointer" 
                            size="sm"
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {/* Información adicional de ajustes */}
              <div className="mt-4 p-4 bg-light rounded-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted fw-semibold">Balance de Ajustes:</span>
                  <span className={`fw-bold fs-5 ${(dashboardMetrics.ajusteEntrada - dashboardMetrics.ajusteSalida) >= 0 ? 'text-success' : 'text-danger'}`}>
                    {isLoading ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="text-muted small">Calculando...</span>
                      </div>
                    ) : (
                      <>
                        {(dashboardMetrics.ajusteEntrada - dashboardMetrics.ajusteSalida) >= 0 ? '+' : ''}
                        {(dashboardMetrics.ajusteEntrada - dashboardMetrics.ajusteSalida).toLocaleString()} unidades
                      </>
                    )}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6} className="mb-4">
          <Card className={`h-100 border-0 shadow-sm ${isLoading ? 'opacity-75' : ''}`}>
            <Card.Header className="bg-transparent border-0 py-4">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faChartLine} className="me-3 text-success" size="lg" />
                <h4 className="mb-0 fw-bold">Resumen de Facturación</h4>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <div className="row mb-4">
                <div className="col-6">
                  <div className="text-center p-4 bg-light rounded-3 h-100">
                    <h3 className="text-success mb-2 fw-bold">
                      {renderValueWithLoader(dashboardMetrics.facturado)}
                    </h3>
                    <div className="d-flex align-items-center justify-content-center">
                      <small className="text-muted me-2">Facturado</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-facturado">
                            {tooltips.facturado}
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon 
                          icon={faInfoCircle} 
                          className="text-info cursor-pointer" 
                          size="sm"
                        />
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-center p-4 bg-light rounded-3 h-100">
                    <h3 className="text-warning mb-2 fw-bold">
                      {renderValueWithLoader(cantidadPorFacturarReal)}
                    </h3>
                    <div className="d-flex align-items-center justify-content-center">
                      <small className="text-muted me-2">Por Facturar</small>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-por-facturar">
                            {tooltips.cantidadPorFacturarReal}
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon 
                          icon={faInfoCircle} 
                          className="text-info cursor-pointer" 
                          size="sm"
                        />
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Porcentaje de facturación */}
              <div className="p-4 bg-gradient rounded-3 text-center position-relative overflow-hidden"
                   style={{background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'}}>
                <div className="text-white">
                  <h2 className="mb-2 fw-bold">
                    {isLoading ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="small">Calculando...</span>
                      </div>
                    ) : (
                      `${porcentajeFacturacion}%`
                    )}
                  </h2>
                  <p className="mb-0 opacity-90">Porcentaje de Facturación</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Botón de WhatsApp para soporte */}
      <div className="position-fixed" style={{ bottom: '30px', left: '30px', zIndex: 1000 }}>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="tooltip-whatsapp">
              ¿Necesitas ayuda? Contáctanos por WhatsApp
            </Tooltip>
          }
        >
          <Button
            variant="success"
            className="rounded-circle shadow-lg"
            style={{ 
              width: '60px', 
              height: '60px',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              border: 'none',
              transition: 'all 0.3s ease'
            }}
            onClick={openWhatsApp}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <FontAwesomeIcon icon={faComments} size="lg" className="text-white" />
          </Button>
        </OverlayTrigger>
        </div>
        </>
      )}

      {/* Estilos CSS adicionales */}
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        .client-dashboard-charts * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .hover-shadow-lg:hover {
          box-shadow: 0 0.75rem 2rem rgba(0,0,0,.125) !important;
          transform: translateY(-2px);
        }
        
        .transition-all {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .progress-bar-animated {
          animation: progress-bar-stripes 1s linear infinite;
        }
        
        .client-dashboard-charts .card {
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,.05);
        }
        
        .client-dashboard-charts .card-body {
          position: relative;
        }
        
        .client-dashboard-charts h2 {
          font-size: 2rem;
          font-weight: 600;
          letter-spacing: -0.025em;
        }
        
        .client-dashboard-charts .fa-icon {
          opacity: 0.85;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .client-dashboard-charts .card:hover .fa-icon {
          opacity: 1;
          transform: scale(1.08);
        }
        
        .client-dashboard-charts p.small {
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.01em;
        }
        
        .bg-gradient {
          background: linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary-dark) 100%);
        }
        
        .row.mb-4 {
          margin-bottom: 1.5rem !important;
        }
        
        @keyframes progress-bar-stripes {
          0% {
            background-position: 1rem 0;
          }
          100% {
            background-position: 0 0;
          }
        }
        
        @media (max-width: 768px) {
          .client-dashboard-charts h2 {
            font-size: 1.75rem;
          }
          
          .client-dashboard-charts .card-body {
            padding: 1rem;
          }
          
          .client-dashboard-charts .fa-icon {
            font-size: 1.5rem !important;
          }
        }
        
        @media (max-width: 576px) {
          .client-dashboard-charts h2 {
            font-size: 1.5rem;
          }
          
          .client-dashboard-charts .card-body {
            padding: 0.75rem;
          }
          
          .row.mb-4 {
            margin-bottom: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientDashboardChart; 